import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, ExternalLink, Star, Filter } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function StockScreener({ user, onLogout }) {
  const navigate = useNavigate();
  const [curatedLists, setCuratedLists] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSector, setSelectedSector] = useState('all');
  const [sortBy, setSortBy] = useState('none');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [listsRes, watchlistRes] = await Promise.allSettled([
        axios.get(`${API}/discover/curated-lists`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/watchlist`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      if (listsRes.status === 'fulfilled') {
        setCuratedLists(listsRes.value.data.lists || []);
      }
      
      if (watchlistRes.status === 'fulfilled') {
        setWatchlist(watchlistRes.value.data.watchlist || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToWatchlist = async (stock) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/watchlist`, stock, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWatchlist([...watchlist, stock]);
    } catch (error) {
      console.error('Error adding to watchlist:', error);
    }
  };

  const handleRemoveFromWatchlist = async (symbol) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/watchlist/${symbol}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWatchlist(watchlist.filter(s => s.symbol !== symbol));
    } catch (error) {
      console.error('Error removing from watchlist:', error);
    }
  };

  const isInWatchlist = (symbol) => watchlist.some(s => s.symbol === symbol);

  const handleAddToPortfolio = (stock) => {
    navigate('/investments', { state: { prefillStock: stock } });
  };

  // Filter and sort logic
  const getFilteredStocks = (stocks) => {
    let filtered = stocks;
    
    if (searchQuery) {
      filtered = filtered.filter(s => 
        s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.symbol.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    if (selectedSector !== 'all') {
      filtered = filtered.filter(s => s.sector === selectedSector);
    }
    
    if (sortBy === 'price-high') {
      filtered = [...filtered].sort((a, b) => b.price - a.price);
    } else if (sortBy === 'price-low') {
      filtered = [...filtered].sort((a, b) => a.price - b.price);
    } else if (sortBy === 'change') {
      filtered = [...filtered].sort((a, b) => b.change_percent - a.change_percent);
    }
    
    return filtered;
  };

  const renderStockCard = (stock, stockIdx) => (
    <div
      key={stockIdx}
      className="p-5 bg-white dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 hover:shadow-lg transition-shadow duration-200"
    >
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100">{stock.name}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">{stock.sector}</p>
        </div>
        <div className="text-right">
          <p className="text-xl font-bold text-gray-800 dark:text-gray-100">₹{stock.price.toFixed(2)}</p>
          <p className={`text-sm font-semibold ${stock.change_percent >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {stock.change_percent >= 0 ? '+' : ''}{stock.change_percent.toFixed(2)}%
          </p>
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-3 mb-4 text-sm">
        <div>
          <p className="text-gray-500 dark:text-gray-400">Market Cap</p>
          <p className="font-semibold text-gray-800 dark:text-gray-100">{stock.market_cap}</p>
        </div>
        <div>
          <p className="text-gray-500 dark:text-gray-400">P/E Ratio</p>
          <p className="font-semibold text-gray-800 dark:text-gray-100">{stock.pe_ratio}</p>
        </div>
        <div>
          <p className="text-gray-500 dark:text-gray-400">Div Yield</p>
          <p className="font-semibold text-gray-800 dark:text-gray-100">{stock.div_yield}%</p>
        </div>
      </div>
      
      <div className="flex space-x-2">
        <Button
          onClick={() => handleAddToPortfolio(stock)}
          className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add to Portfolio
        </Button>
        <Button
          variant={isInWatchlist(stock.symbol) ? "default" : "outline"}
          onClick={() => isInWatchlist(stock.symbol) ? handleRemoveFromWatchlist(stock.symbol) : handleAddToWatchlist(stock)}
          className={isInWatchlist(stock.symbol) ? "bg-yellow-500 hover:bg-yellow-600" : "dark:bg-gray-600 dark:text-gray-100"}
        >
          <Star className="w-4 h-4" />
        </Button>
        <Button
          variant="outline"
          onClick={() => window.open(`https://www.google.com/finance/quote/${stock.symbol}:NSE`, '_blank')}
          className="dark:bg-gray-600 dark:text-gray-100 dark:border-gray-500"
        >
          <ExternalLink className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  const renderFilterControls = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <div>
        <label className="text-sm text-gray-600 dark:text-gray-400 mb-2 block">Search</label>
        <Input
          placeholder="Search stocks..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="dark:bg-gray-700 dark:text-gray-100"
        />
      </div>
      <div>
        <label className="text-sm text-gray-600 dark:text-gray-400 mb-2 block">Sector</label>
        <Select value={selectedSector} onValueChange={setSelectedSector}>
          <SelectTrigger className="dark:bg-gray-700 dark:text-gray-100">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sectors</SelectItem>
            <SelectItem value="Banking">Banking</SelectItem>
            <SelectItem value="IT Services">IT Services</SelectItem>
            <SelectItem value="Oil & Gas">Oil & Gas</SelectItem>
            <SelectItem value="FMCG">FMCG</SelectItem>
            <SelectItem value="Pharma">Pharma</SelectItem>
            <SelectItem value="Auto">Auto</SelectItem>
            <SelectItem value="Metals">Metals</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-sm text-gray-600 dark:text-gray-400 mb-2 block">Sort By</label>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="dark:bg-gray-700 dark:text-gray-100">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Default</SelectItem>
            <SelectItem value="price-high">Price: High to Low</SelectItem>
            <SelectItem value="price-low">Price: Low to High</SelectItem>
            <SelectItem value="change">% Change</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen p-4 lg:p-8">
        <Navigation user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 lg:p-8">
      <Navigation user={user} onLogout={onLogout} />
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center">
              <Filter className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100">Stock Screener</h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400 ml-15">Filter and discover stocks across various categories</p>
        </div>

        {/* Tabbed Stock Lists */}
        <Card className="glass p-6 mb-8 dark:bg-gray-800">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="mb-6 overflow-x-auto">
              <TabsList className="inline-flex w-auto min-w-full lg:w-full lg:grid lg:grid-cols-7">
                <TabsTrigger value="all" className="whitespace-nowrap">All Stocks</TabsTrigger>
                <TabsTrigger value="top-nifty-50" className="whitespace-nowrap">Top Nifty 50</TabsTrigger>
                <TabsTrigger value="high-dividend" className="whitespace-nowrap">High Dividend</TabsTrigger>
                <TabsTrigger value="banking-stocks" className="whitespace-nowrap">Banking</TabsTrigger>
                <TabsTrigger value="midcap-gems" className="whitespace-nowrap">Mid-Cap</TabsTrigger>
                <TabsTrigger value="momentum-stocks" className="whitespace-nowrap">Momentum</TabsTrigger>
                <TabsTrigger value="value-picks" className="whitespace-nowrap">Value Picks</TabsTrigger>
              </TabsList>
            </div>

            {/* All Stocks Tab */}
            <TabsContent value="all">
              {renderFilterControls()}
              <div className="space-y-8">
                {curatedLists.map((list, listIdx) => {
                  const filteredStocks = getFilteredStocks(list.stocks);
                  if (filteredStocks.length === 0) return null;
                  
                  return (
                    <div key={listIdx}>
                      <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center">
                        <span className="text-2xl mr-2">{list.icon}</span>
                        {list.title}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{list.description}</p>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {filteredStocks.map((stock, stockIdx) => renderStockCard(stock, stockIdx))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </TabsContent>

            {/* Individual Category Tabs */}
            {curatedLists.map((list) => (
              <TabsContent key={list.id} value={list.id}>
                <div className="mb-4">
                  <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2 flex items-center">
                    <span className="text-3xl mr-2">{list.icon}</span>
                    {list.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">{list.description}</p>
                </div>
                {renderFilterControls()}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {getFilteredStocks(list.stocks).map((stock, stockIdx) => renderStockCard(stock, stockIdx))}
                </div>
                {getFilteredStocks(list.stocks).length === 0 && (
                  <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                    No stocks match your filters. Try adjusting your search criteria.
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </Card>

      </div>

      <Footer />
    </div>
  );
}
